// Modul pro správu odběratelů/zákazníků
// - Seznam, vyhledávání, vytvoření, editace, mazání
// - Vyhledávač pro použití v editoru nabídek (selectorModal)

import { icon } from './icons.js';

const API_BASE_URL =
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'http://localhost:1234/api'
    : '/api';

export class CustomersManager {
  constructor() {
    this.customers = [];
    this.searchQuery = '';
    this.loading = false;
    this.error = null;
    this._loaded = false;
  }

  // ----- API -----

  async fetchCustomers(search = '') {
    this.loading = true;
    this.error = null;
    try {
      const url = new URL(`${API_BASE_URL}/customers`, window.location.origin);
      if (search) url.searchParams.set('search', search);
      // Při použití absolutní URL ignorujeme window.location.origin u prefixu localhost:1234
      const fetchUrl = search ? `${API_BASE_URL}/customers?search=${encodeURIComponent(search)}` : `${API_BASE_URL}/customers`;
      const res = await fetch(fetchUrl, { credentials: 'include' });
      if (!res.ok) {
        if (res.status === 401) throw new Error('Pro přístup k zákazníkům je třeba být přihlášen');
        throw new Error('Nepodařilo se načíst zákazníky');
      }
      const json = await res.json();
      this.customers = json.data || [];
      this._loaded = true;
      return this.customers;
    } catch (err) {
      this.error = err.message;
      this.customers = [];
      throw err;
    } finally {
      this.loading = false;
    }
  }

  async getCustomer(id) {
    const res = await fetch(`${API_BASE_URL}/customers/${id}`, { credentials: 'include' });
    if (!res.ok) throw new Error('Nepodařilo se načíst zákazníka');
    const json = await res.json();
    return json.data;
  }

  async fetchCustomerStats(id) {
    const res = await fetch(`${API_BASE_URL}/customers/${id}/stats`, { credentials: 'include' });
    if (!res.ok) throw new Error('Nepodařilo se načíst detail zákazníka');
    const json = await res.json();
    return json.data;
  }

  async createCustomer(data) {
    const res = await fetch(`${API_BASE_URL}/customers`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Nepodařilo se vytvořit zákazníka');
    return json.data;
  }

  async updateCustomer(id, data) {
    const res = await fetch(`${API_BASE_URL}/customers/${id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Nepodařilo se aktualizovat zákazníka');
    return json.data;
  }

  async deleteCustomer(id) {
    const res = await fetch(`${API_BASE_URL}/customers/${id}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!res.ok) {
      const json = await res.json().catch(() => ({}));
      throw new Error(json.error || 'Nepodařilo se smazat zákazníka');
    }
    return true;
  }

  async searchAres(query, limit = 10) {
    const url = `${API_BASE_URL}/ares/search?q=${encodeURIComponent(query)}&limit=${limit}`;
    const res = await fetch(url, { credentials: 'include' });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.success === false) {
      throw new Error(json.message || 'Vyhledávání v ARESu selhalo');
    }
    return json.data || [];
  }

  // ----- Render seznamu -----

  async showSection() {
    await this.loadAndRender();
  }

  async loadAndRender() {
    const container = document.getElementById('zakaznici-section-content');
    if (!container) return;

    container.innerHTML = this.renderHeader() + `<div id="customers-list-container">${this.renderLoading()}</div>`;
    this.bindHeaderEvents();

    try {
      await this.fetchCustomers(this.searchQuery);
      this.renderList();
    } catch (err) {
      this.renderError(err.message);
    }
  }

  renderHeader() {
    return `
      <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
        <div>
          <h2 class="text-2xl font-semibold text-gray-900 mb-1">Zákazníci / odběratelé</h2>
          <p class="text-gray-600 mb-0 text-sm">Správa kontaktů a fakturačních údajů.</p>
        </div>
        <div class="flex w-full sm:w-auto gap-2">
          <div class="relative flex-1 sm:flex-none">
            <input
              type="text"
              id="customers-search-input"
              placeholder="Hledat (název, IČO, město, email)…"
              value="${this.escape(this.searchQuery)}"
              class="pl-9 pr-3 py-2 w-full sm:w-72 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
            ${icon('magnifying-glass', 'absolute left-2.5 top-2.5 w-4 h-4 text-gray-400')}
          </div>
          <button id="customers-add-btn"
            class="inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 whitespace-nowrap">
            ${icon('plus', 'w-4 h-4 sm:mr-2')}
            <span class="hidden sm:inline">Přidat zákazníka</span>
            <span class="sm:hidden">Přidat</span>
          </button>
        </div>
      </div>
    `;
  }

  bindHeaderEvents() {
    const input = document.getElementById('customers-search-input');
    if (input) {
      let debounce;
      input.addEventListener('input', (e) => {
        clearTimeout(debounce);
        const value = e.target.value;
        debounce = setTimeout(async () => {
          this.searchQuery = value;
          try {
            await this.fetchCustomers(this.searchQuery);
            this.renderList();
          } catch (err) {
            this.renderError(err.message);
          }
        }, 200);
      });
    }
    const addBtn = document.getElementById('customers-add-btn');
    if (addBtn) addBtn.addEventListener('click', () => this.openEditor());
  }

  renderLoading() {
    return `
      <div class="flex justify-center p-12">
        ${icon('spinner', 'animate-spin h-8 w-8 text-blue-500')}
      </div>
    `;
  }

  renderError(message) {
    const container = document.getElementById('customers-list-container');
    if (!container) return;
    container.innerHTML = `
      <div class="bg-red-50 border border-red-200 rounded-md p-4 text-sm text-red-700">
        ${this.escape(message)}
      </div>
    `;
  }

  renderList() {
    const container = document.getElementById('customers-list-container');
    if (!container) return;

    if (this.customers.length === 0) {
      container.innerHTML = `
        <div class="bg-white rounded-lg shadow-md p-8 text-center">
          ${icon('user-group', 'mx-auto h-12 w-12 text-gray-400 mb-4')}
          <h3 class="text-lg font-medium text-gray-900 mb-2">
            ${this.searchQuery ? 'Žádný zákazník neodpovídá hledání' : 'Zatím nemáte žádné zákazníky'}
          </h3>
          <p class="text-gray-500 text-sm">
            ${this.searchQuery ? 'Zkuste jiný hledaný výraz nebo přidejte nového zákazníka.' : 'Přidejte prvního zákazníka kliknutím na tlačítko vpravo nahoře.'}
          </p>
        </div>
      `;
      return;
    }

    container.innerHTML = `
      <div class="bg-white shadow overflow-hidden sm:rounded-md">
        <ul class="divide-y divide-gray-200">
          ${this.customers.map((c) => this.renderCustomerRow(c)).join('')}
        </ul>
      </div>
    `;

    // Bind action handlers
    container.querySelectorAll('[data-action="detail"]').forEach((btn) => {
      btn.addEventListener('click', () => this.openCustomerDetail(btn.dataset.id));
    });
    container.querySelectorAll('[data-action="edit"]').forEach((btn) => {
      btn.addEventListener('click', () => this.openEditor(btn.dataset.id));
    });
    container.querySelectorAll('[data-action="delete"]').forEach((btn) => {
      btn.addEventListener('click', () => this.confirmDelete(btn.dataset.id));
    });
    // Klik na řádek (kromě tlačítek) = detail
    container.querySelectorAll('li > div').forEach((row) => {
      row.addEventListener('click', (e) => {
        if (e.target.closest('[data-action]')) return;
        const li = row.closest('li');
        const detailBtn = li?.querySelector('[data-action="detail"]');
        if (detailBtn) this.openCustomerDetail(detailBtn.dataset.id);
      });
    });
  }

  // ----- Detail zákazníka + analytika -----

  async openCustomerDetail(id) {
    if (window.sectionManager) window.sectionManager.showSection('zakaznik-detail');
    const container = document.getElementById('zakaznik-detail-content');
    if (!container) return;
    container.innerHTML = this.renderLoading();

    try {
      const data = await this.fetchCustomerStats(id);
      this.renderCustomerDetail(container, data);
    } catch (err) {
      container.innerHTML = `<div class="bg-red-50 border border-red-200 rounded-md p-4 text-sm text-red-700">${this.escape(err.message)}</div>`;
    }
  }

  renderCustomerDetail(container, data) {
    const { customer, stats, offers, orders, invoices = [] } = data;
    const offerStatusMeta = (window.OffersListManager?.STATUS_META) || {};
    const orderStatusMeta = (window.OrdersListManager?.STATUS_META) || {};
    const invoiceStatusMeta = (window.InvoicesListManager?.STATUS_META) || {};

    const addressLine = [customer.address, customer.zip, customer.city].filter(Boolean).join(', ');
    const fmt = (n) => Number(n || 0).toLocaleString('cs-CZ', { maximumFractionDigits: 0 });

    container.innerHTML = `
      <div class="space-y-4">
        <button id="customer-detail-back"
          class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
          ${icon('chevron-left', 'w-4 h-4 mr-2')}
          Zpět na zákazníky
        </button>

        <div class="bg-white rounded-lg shadow border border-gray-200 p-6">
          <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div class="min-w-0">
              <div class="flex items-center gap-2 flex-wrap">
                <h2 class="text-2xl font-semibold text-gray-900">${this.escape(customer.name)}</h2>
                <span class="text-xs px-2 py-0.5 rounded-full ${customer.type === 'person' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}">
                  ${customer.type === 'person' ? 'Osoba' : 'Firma'}
                </span>
                <span class="text-xs px-2 py-0.5 rounded-full ${customer.pricingTier === 'malo' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}" title="Cenová úroveň zákazníka">
                  ${customer.pricingTier === 'malo' ? 'Maloobchodní' : 'Velkoobchodní'}
                </span>
              </div>
              ${addressLine ? `<p class="mt-1 text-sm text-gray-600">${this.escape(addressLine)}</p>` : ''}
              ${(customer.ico || customer.dic) ? `<p class="mt-1 text-xs text-gray-500">${customer.ico ? `IČO: ${this.escape(customer.ico)}` : ''}${customer.ico && customer.dic ? ' · ' : ''}${customer.dic ? `DIČ: ${this.escape(customer.dic)}` : ''}</p>` : ''}
              ${(customer.email || customer.phone) ? `<p class="mt-1 text-xs text-gray-500">${customer.email ? this.escape(customer.email) : ''}${customer.email && customer.phone ? ' · ' : ''}${customer.phone ? this.escape(customer.phone) : ''}</p>` : ''}
              ${customer.note ? `<p class="mt-2 text-sm text-gray-700 italic">"${this.escape(customer.note)}"</p>` : ''}
            </div>
            <div class="flex gap-2 shrink-0">
              <button id="customer-detail-edit"
                class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                ${icon('pencil-square', 'w-4 h-4 mr-2')}
                Upravit
              </button>
            </div>
          </div>
        </div>

        <!-- Analytika -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          ${this.renderStatCard({
            title: 'Nabídky',
            count: stats.offers.count,
            sum: stats.offers.totalPrice,
            avg: stats.offers.averagePrice,
            color: 'blue',
            icon: 'document-text'
          })}
          ${this.renderStatCard({
            title: 'Objednávky',
            count: stats.orders.count,
            sum: stats.orders.totalPrice,
            avg: stats.orders.averagePrice,
            color: 'emerald',
            icon: 'clipboard-check'
          })}
          ${this.renderInvoiceStatCard(stats.invoices)}
        </div>

        <!-- Distribuce stavů -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div class="bg-white rounded-lg shadow border border-gray-200 p-4">
            <h3 class="text-sm font-semibold text-gray-700 mb-3">Stavy nabídek</h3>
            ${this.renderStatusDistribution(stats.offers.byStatus, offerStatusMeta)}
          </div>
          <div class="bg-white rounded-lg shadow border border-gray-200 p-4">
            <h3 class="text-sm font-semibold text-gray-700 mb-3">Stavy objednávek</h3>
            ${this.renderStatusDistribution(stats.orders.byStatus, orderStatusMeta)}
          </div>
          <div class="bg-white rounded-lg shadow border border-gray-200 p-4">
            <h3 class="text-sm font-semibold text-gray-700 mb-3">Stavy faktur</h3>
            ${this.renderStatusDistribution(stats.invoices.byStatus, invoiceStatusMeta)}
          </div>
        </div>

        <!-- Soubory zákazníka (mini panel – posledních 5) -->
        <div id="customer-files-panel" data-customer-id="${this.escape(customer.id)}" data-customer-name="${this.escape(customer.name)}">
          ${this.renderCustomerFilesSkeleton()}
        </div>

        <!-- Historie nabídek -->
        <div class="bg-white rounded-lg shadow border border-gray-200">
          <div class="px-4 py-3 border-b border-gray-200">
            <h3 class="text-sm font-semibold text-gray-700">Historie nabídek (${offers.length})</h3>
          </div>
          ${offers.length === 0
            ? `<p class="p-4 text-sm text-gray-500">Žádné nabídky pro tohoto zákazníka.</p>`
            : `<ul class="divide-y divide-gray-200">${offers.slice(0, 50).map((o) => this.renderOfferHistoryRow(o, offerStatusMeta)).join('')}</ul>`}
        </div>

        <!-- Historie objednávek -->
        <div class="bg-white rounded-lg shadow border border-gray-200">
          <div class="px-4 py-3 border-b border-gray-200">
            <h3 class="text-sm font-semibold text-gray-700">Historie objednávek (${orders.length})</h3>
          </div>
          ${orders.length === 0
            ? `<p class="p-4 text-sm text-gray-500">Žádné objednávky pro tohoto zákazníka.</p>`
            : `<ul class="divide-y divide-gray-200">${orders.slice(0, 50).map((o) => this.renderOrderHistoryRow(o, orderStatusMeta)).join('')}</ul>`}
        </div>

        <!-- Historie faktur -->
        <div class="bg-white rounded-lg shadow border border-gray-200">
          <div class="px-4 py-3 border-b border-gray-200">
            <h3 class="text-sm font-semibold text-gray-700">Historie faktur (${invoices.length})</h3>
          </div>
          ${invoices.length === 0
            ? `<p class="p-4 text-sm text-gray-500">Žádné faktury pro tohoto zákazníka.</p>`
            : `<ul class="divide-y divide-gray-200">${invoices.slice(0, 50).map((inv) => this.renderInvoiceHistoryRow(inv, invoiceStatusMeta)).join('')}</ul>`}
        </div>
      </div>
    `;

    document.getElementById('customer-detail-back')?.addEventListener('click', () => {
      if (window.sectionManager) window.sectionManager.showSection('zakaznici');
    });
    document.getElementById('customer-detail-edit')?.addEventListener('click', () => this.openEditor(customer.id));

    // Načti soubory zákazníka asynchronně (panel se rerenderuje, jakmile dorazí data)
    this.loadCustomerFiles(customer.id, customer.name).catch((err) => {
      console.warn('Nepodařilo se načíst soubory zákazníka:', err);
    });
  }

  // ----- Detail zákazníka: panel "Soubory" -----

  renderCustomerFilesSkeleton() {
    return `
      <div class="bg-white rounded-lg shadow border border-gray-200">
        <div class="px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-3">
          <h3 class="text-sm font-semibold text-gray-700 flex items-center gap-2">
            ${icon('folder', 'w-4 h-4 text-gray-400')}
            Soubory
          </h3>
          <div class="flex items-center gap-2 text-xs text-gray-400">Načítám…</div>
        </div>
        <div class="p-4">
          <div class="space-y-2">
            ${[1, 2, 3].map(() => `<div class="h-12 bg-gray-100 rounded animate-pulse"></div>`).join('')}
          </div>
        </div>
      </div>
    `;
  }

  async loadCustomerFiles(customerId, customerName) {
    const API = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? 'http://localhost:1234/api'
      : '/api';
    const panel = document.getElementById('customer-files-panel');
    if (!panel) return;

    try {
      const res = await fetch(`${API}/files?customerId=${encodeURIComponent(customerId)}`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const files = Array.isArray(json.data) ? json.data : [];
      this.renderCustomerFilesPanel(panel, customerId, customerName, files);
    } catch (err) {
      console.warn('Soubory zákazníka:', err);
      panel.innerHTML = `
        <div class="bg-white rounded-lg shadow border border-gray-200 p-4 text-sm text-gray-500">
          Soubory se nepodařilo načíst.
        </div>
      `;
    }
  }

  renderCustomerFilesPanel(panel, customerId, customerName, files) {
    const totalCount = files.length;
    const totalSize = files.reduce((sum, f) => sum + (Number(f.size) || 0), 0);
    const recent = files.slice(0, 5); // listFiles na backendu už řadí newest-first

    const rows = recent.length > 0
      ? recent.map((f) => this.renderCustomerFilesRow(f)).join('')
      : `<li class="px-4 py-6 text-sm text-gray-500 text-center">Tento zákazník zatím nemá nahrané žádné soubory.</li>`;

    panel.innerHTML = `
      <div class="bg-white rounded-lg shadow border border-gray-200">
        <div class="px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-3 flex-wrap">
          <h3 class="text-sm font-semibold text-gray-700 flex items-center gap-2">
            ${icon('folder', 'w-4 h-4 text-gray-400')}
            Soubory
            <span class="text-xs font-normal text-gray-400">
              ${totalCount > 0 ? `(${totalCount} · ${this.formatFileSize(totalSize)})` : ''}
            </span>
          </h3>
          <div class="flex items-center gap-2 flex-wrap">
            <button type="button" id="customer-files-upload-btn"
              class="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 shadow-sm">
              ${icon('cloud-arrow-up', 'w-3.5 h-3.5 mr-1.5')}
              Nahrát
            </button>
            ${totalCount > 0 ? `
              <button type="button" id="customer-files-share-btn"
                class="inline-flex items-center px-2.5 py-1.5 border border-emerald-300 text-xs font-medium rounded-md text-emerald-700 bg-white hover:bg-emerald-50">
                ${icon('link', 'w-3.5 h-3.5 mr-1.5')}
                Sdílet odkaz
              </button>` : ''}
            <button type="button" id="customer-files-all-btn"
              class="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
              Všechny soubory
              ${icon('chevron-right', 'w-3.5 h-3.5 ml-1.5')}
            </button>
          </div>
        </div>
        <ul class="divide-y divide-gray-100">${rows}</ul>
        ${totalCount > recent.length ? `
          <div class="px-4 py-2 border-t border-gray-100 text-xs text-gray-500 text-center bg-gray-50">
            Zobrazeno posledních ${recent.length} z ${totalCount} souborů.
            <button id="customer-files-all-link" class="inline-flex items-center gap-0.5 text-blue-600 hover:underline ml-1">Zobrazit všechny ${icon('chevron-right', 'w-3 h-3')}</button>
          </div>` : ''}
      </div>
    `;

    this.bindCustomerFilesEvents(panel, customerId, customerName);
  }

  renderCustomerFilesRow(file) {
    const ext = (() => {
      const name = file.originalName || '';
      const idx = name.lastIndexOf('.');
      return idx >= 0 ? name.slice(idx + 1).toUpperCase() : '';
    })();
    return `
      <li class="px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors">
        <div class="flex-shrink-0 w-9 h-9 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center text-[10px] font-semibold uppercase tracking-wide">
          ${ext ? this.escape(ext.slice(0, 4)) : icon('document', 'w-4 h-4')}
        </div>
        <div class="flex-1 min-w-0">
          <div class="text-sm font-medium text-gray-900 truncate" title="${this.escape(file.originalName || '')}">
            ${this.escape(file.originalName || '')}
          </div>
          <div class="text-xs text-gray-500 truncate">
            ${this.escape(this.formatFileSize(file.size))} · ${this.escape(this.formatDateTime(file.uploadedAt))}
            ${file.description ? ` · ${this.escape(file.description)}` : ''}
          </div>
        </div>
      </li>
    `;
  }

  bindCustomerFilesEvents(panel, customerId, customerName) {
    const goToFiles = () => {
      // Předem nastav filtr na zákazníka, pak přepni
      if (window.filesManager) {
        window.filesManager.selectedCustomer = { id: customerId, name: customerName };
        window.filesManager.updateCustomerLabel?.();
      }
      if (window.sectionManager) window.sectionManager.showSection('soubory');
    };

    panel.querySelector('#customer-files-all-btn')?.addEventListener('click', goToFiles);
    panel.querySelector('#customer-files-all-link')?.addEventListener('click', goToFiles);

    panel.querySelector('#customer-files-upload-btn')?.addEventListener('click', () => {
      // Otevři přímo upload modal s předvyplněným zákazníkem
      if (!window.filesManager) {
        this.notify?.('Sekce souborů ještě není inicializovaná', 'warning');
        return;
      }
      window.filesManager.openUploadModal({
        customer: { id: customerId, name: customerName }
      });
      // Po zavření modálu obnov i tento panel
      const refresh = () => this.loadCustomerFiles(customerId, customerName);
      // Polling – zjisti, kdy modal zmizí, a refreshni panel
      const interval = setInterval(() => {
        if (!document.getElementById('files-upload-modal')) {
          clearInterval(interval);
          refresh();
        }
      }, 300);
    });

    panel.querySelector('#customer-files-share-btn')?.addEventListener('click', async () => {
      try {
        const API = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
          ? 'http://localhost:1234/api'
          : '/api';
        const res = await fetch(`${API}/files/share/${encodeURIComponent(customerId)}`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ customerName })
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const url = json.data?.url;
        if (!url) throw new Error('Server nevrátil URL');
        try {
          await navigator.clipboard.writeText(url);
          this.notify?.(`Odkaz pro ${customerName} byl zkopírován do schránky`, 'success');
        } catch (_) {
          window.prompt('Veřejný odkaz (zkopírujte ručně):', url);
        }
      } catch (err) {
        console.error('Sdílení selhalo:', err);
        this.notify?.('Nepodařilo se vytvořit veřejný odkaz', 'error');
      }
    });
  }

  formatFileSize(bytes) {
    const n = Number(bytes) || 0;
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
    return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }

  formatDateTime(iso) {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString('cs-CZ', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch (_) {
      return String(iso);
    }
  }

  renderStatCard({ title, count, sum, avg, color, icon: iconName, placeholder = false }) {
    const colors = {
      blue:    { bg: 'bg-blue-50',    text: 'text-blue-700',    icon: 'text-blue-500' },
      emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', icon: 'text-emerald-500' },
      gray:    { bg: 'bg-gray-50',    text: 'text-gray-500',    icon: 'text-gray-400' }
    };
    const c = colors[color] || colors.gray;
    const fmt = (n) => Number(n || 0).toLocaleString('cs-CZ', { maximumFractionDigits: 0 });

    return `
      <div class="bg-white rounded-lg shadow border border-gray-200 p-5 ${placeholder ? 'opacity-60' : ''}">
        <div class="flex items-start justify-between">
          <div>
            <div class="text-xs uppercase font-semibold tracking-wider ${c.text}">${this.escape(title)}</div>
            <div class="mt-2 text-3xl font-bold text-gray-900">${count.toLocaleString('cs-CZ')}</div>
            <div class="mt-3 text-sm text-gray-700"><strong>${fmt(sum)} Kč</strong> celkem</div>
            ${count > 0 && avg > 0 ? `<div class="text-xs text-gray-500">průměr ${fmt(avg)} Kč / ks</div>` : ''}
            ${placeholder ? `<div class="text-xs text-gray-400 mt-1">Sekce v přípravě (Fáze 3)</div>` : ''}
          </div>
          <div class="${c.bg} ${c.icon} rounded-lg p-2">
            ${icon(iconName, 'w-5 h-5')}
          </div>
        </div>
      </div>
    `;
  }

  renderStatusDistribution(byStatus, meta) {
    const entries = Object.entries(byStatus || {});
    if (entries.length === 0) return `<p class="text-sm text-gray-400">Žádná data</p>`;
    const total = entries.reduce((s, [, c]) => s + c, 0);
    return `
      <div class="space-y-2">
        ${entries.map(([key, count]) => {
          const m = meta[key] || { label: key, dot: 'bg-gray-400' };
          const pct = total > 0 ? Math.round((count / total) * 100) : 0;
          return `
            <div>
              <div class="flex items-center justify-between text-xs mb-1">
                <span class="flex items-center text-gray-700">
                  <span class="w-2 h-2 rounded-full mr-2 ${m.dot}"></span>${this.escape(m.label)}
                </span>
                <span class="text-gray-500">${count} (${pct} %)</span>
              </div>
              <div class="w-full bg-gray-100 rounded-full h-1.5">
                <div class="${m.dot} h-1.5 rounded-full" style="width: ${pct}%"></div>
              </div>
            </div>
          `;
        }).join('')}
      </div>
    `;
  }

  renderOfferHistoryRow(o, statusMeta) {
    const m = statusMeta[o.status] || { label: o.status || 'draft', badge: 'bg-gray-100 text-gray-800', dot: 'bg-gray-500' };
    const date = new Date(o.createdAt).toLocaleDateString('cs-CZ');
    return `
      <li class="px-4 py-3 hover:bg-gray-50">
        <button class="w-full text-left flex items-center justify-between gap-3" onclick="window.offersListManager?.openOfferEditor('${o.id}')">
          <div class="min-w-0">
            <div class="flex items-center gap-2">
              <span class="text-sm font-medium text-gray-900">#${this.escape(o.id)}</span>
              <span class="inline-flex items-center text-xs px-2 py-0.5 rounded-full ${m.badge}">
                <span class="w-1.5 h-1.5 rounded-full mr-1.5 ${m.dot}"></span>${this.escape(m.label)}
              </span>
            </div>
            <div class="text-xs text-gray-500">${date} · ${(o.items || []).length} položek</div>
          </div>
          <div class="text-sm font-semibold text-gray-900 shrink-0">${(o.totalPrice || 0).toLocaleString('cs-CZ')} Kč</div>
        </button>
      </li>
    `;
  }

  renderOrderHistoryRow(o, statusMeta) {
    const m = statusMeta[o.status] || { label: o.status || 'received', badge: 'bg-gray-100 text-gray-800', dot: 'bg-gray-500' };
    const date = new Date(o.createdAt).toLocaleDateString('cs-CZ');
    return `
      <li class="px-4 py-3 hover:bg-gray-50">
        <button class="w-full text-left flex items-center justify-between gap-3" onclick="window.ordersListManager?.openDetail('${o.id}')">
          <div class="min-w-0">
            <div class="flex items-center gap-2">
              <span class="text-sm font-medium text-gray-900">#${this.escape(o.id)}</span>
              <span class="inline-flex items-center text-xs px-2 py-0.5 rounded-full ${m.badge}">
                <span class="w-1.5 h-1.5 rounded-full mr-1.5 ${m.dot}"></span>${this.escape(m.label)}
              </span>
              ${o.invoicedAsId ? `<span class="inline-flex items-center gap-0.5 text-xs text-indigo-600">${icon('arrow-right', 'w-3 h-3')} ${this.escape(o.invoicedAsId)}</span>` : ''}
            </div>
            <div class="text-xs text-gray-500">${date} · ${(o.items || []).length} položek${o.offerId ? ` · z nabídky #${this.escape(o.offerId)}` : ''}</div>
          </div>
          <div class="text-sm font-semibold text-gray-900 shrink-0">${(o.totalPrice || 0).toLocaleString('cs-CZ')} Kč</div>
        </button>
      </li>
    `;
  }

  renderInvoiceStatCard(invStats) {
    const fmt = (n) => Number(n || 0).toLocaleString('cs-CZ', { maximumFractionDigits: 0 });
    const count = invStats?.count || 0;
    const total = invStats?.totalPrice || 0;
    const paid = invStats?.paid || 0;
    const unpaid = invStats?.unpaid || 0;
    const overdue = invStats?.overdueCount || 0;
    const overdueAmount = invStats?.overdueAmount || 0;

    return `
      <div class="bg-white rounded-lg shadow border border-gray-200 p-5">
        <div class="flex items-start justify-between">
          <div class="min-w-0">
            <div class="text-xs uppercase font-semibold tracking-wider text-indigo-700">Faktury</div>
            <div class="mt-2 text-3xl font-bold text-gray-900">${count.toLocaleString('cs-CZ')}</div>
            <div class="mt-3 text-sm text-gray-700"><strong>${fmt(total)} Kč</strong> celkem (s DPH)</div>
            <div class="mt-2 grid grid-cols-2 gap-2 text-xs">
              <div class="text-emerald-700">Uhrazeno: <strong>${fmt(paid)} Kč</strong></div>
              <div class="${unpaid > 0 ? 'text-amber-700' : 'text-gray-500'}">Zbývá: <strong>${fmt(unpaid)} Kč</strong></div>
            </div>
            ${overdue > 0 ? `
              <div class="mt-2 text-xs text-red-700 bg-red-50 rounded px-2 py-1 inline-block">
                ${overdue}× po splatnosti · ${fmt(overdueAmount)} Kč
              </div>
            ` : ''}
          </div>
          <div class="bg-indigo-50 text-indigo-500 rounded-lg p-2">
            ${icon('receipt', 'w-5 h-5')}
          </div>
        </div>
      </div>
    `;
  }

  renderInvoiceHistoryRow(inv, statusMeta) {
    const m = statusMeta[inv.status] || { label: inv.status || 'draft', badge: 'bg-gray-100 text-gray-800', dot: 'bg-gray-500' };
    const issueDate = inv.issueDate ? new Date(inv.issueDate).toLocaleDateString('cs-CZ') : '—';
    const dueDate = inv.dueDate ? new Date(inv.dueDate).toLocaleDateString('cs-CZ') : '—';
    const total = inv.totalPriceWithVat || 0;
    const paid = inv.paidAmount || 0;
    const remaining = Math.max(total - paid, 0);
    return `
      <li class="px-4 py-3 hover:bg-gray-50">
        <button class="w-full text-left flex items-center justify-between gap-3" onclick="(window.sectionManager?.showSection('faktury'),window.invoicesListManager?.openDetail('${inv.id}'))">
          <div class="min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <span class="text-sm font-medium text-gray-900">${this.escape(inv.id)}</span>
              <span class="inline-flex items-center text-xs px-2 py-0.5 rounded-full ${m.badge}">
                <span class="w-1.5 h-1.5 rounded-full mr-1.5 ${m.dot}"></span>${this.escape(m.label)}
              </span>
              ${inv.isOverdue ? `<span class="inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800">Po splatnosti</span>` : ''}
            </div>
            <div class="text-xs text-gray-500">Vystaveno ${issueDate} · Splatnost ${dueDate}${inv.orderId ? ` · z objednávky ${this.escape(inv.orderId)}` : ''}</div>
          </div>
          <div class="text-right shrink-0">
            <div class="text-sm font-semibold text-gray-900">${total.toLocaleString('cs-CZ', { maximumFractionDigits: 2 })} ${this.escape(inv.currency || 'CZK')}</div>
            ${remaining > 0
              ? `<div class="text-xs text-amber-700">Zbývá ${remaining.toLocaleString('cs-CZ', { maximumFractionDigits: 2 })} ${this.escape(inv.currency || 'CZK')}</div>`
              : `<div class="text-xs text-emerald-700">Uhrazeno</div>`}
          </div>
        </button>
      </li>
    `;
  }

  renderCustomerRow(c) {
    const addressLine = [c.address, c.zip, c.city].filter(Boolean).join(', ');
    const ico = c.ico ? `IČO: ${this.escape(c.ico)}` : '';
    const dic = c.dic ? `DIČ: ${this.escape(c.dic)}` : '';
    const taxLine = [ico, dic].filter(Boolean).join(' · ');

    return `
      <li class="hover:bg-gray-50 transition-colors">
        <div class="px-4 py-4 sm:px-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div class="min-w-0 flex-1">
            <p class="text-base sm:text-sm font-semibold text-gray-900 leading-snug break-words sm:truncate">
              ${this.escape(c.name)}
            </p>
            <div class="mt-1 flex flex-wrap items-center gap-2">
              <span class="text-xs px-2 py-0.5 rounded-full ${c.type === 'person' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}">
                ${c.type === 'person' ? 'Osoba' : 'Firma'}
              </span>
              <span class="text-xs px-2 py-0.5 rounded-full ${c.pricingTier === 'malo' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}" title="Cenová úroveň">
                ${c.pricingTier === 'malo' ? 'Maloobchod' : 'Velkoobchod'}
              </span>
            </div>
            ${addressLine ? `<p class="mt-1 text-sm text-gray-600 truncate">${this.escape(addressLine)}</p>` : ''}
            ${taxLine ? `<p class="mt-1 text-xs text-gray-500">${taxLine}</p>` : ''}
            ${(c.email || c.phone) ? `
              <p class="mt-1 text-xs text-gray-500">
                ${c.email ? `<span>${this.escape(c.email)}</span>` : ''}
                ${c.email && c.phone ? ' · ' : ''}
                ${c.phone ? `<span>${this.escape(c.phone)}</span>` : ''}
              </p>` : ''}
          </div>
          <div class="flex space-x-2 shrink-0 self-end sm:self-auto">
            <button data-action="detail" data-id="${c.id}"
              class="inline-flex items-center p-2 border border-blue-300 rounded-md shadow-sm text-sm font-medium text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              title="Detail a historie zakázek">
              ${icon('chart-bar', 'h-4 w-4')}
            </button>
            <button data-action="edit" data-id="${c.id}"
              class="inline-flex items-center p-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              title="Upravit">
              ${icon('pencil-square', 'h-4 w-4')}
            </button>
            <button data-action="delete" data-id="${c.id}"
              class="inline-flex items-center p-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              title="Smazat">
              ${icon('trash', 'h-4 w-4')}
            </button>
          </div>
        </div>
      </li>
    `;
  }

  // ----- Editor (modal) -----

  openEditor(id = null) {
    const customer = id ? this.customers.find((c) => c.id === id) : null;
    const isEdit = !!customer;
    const data = customer || {
      name: '', type: 'company', ico: '', dic: '',
      address: '', city: '', zip: '', country: 'Česká republika',
      contactPerson: '', email: '', phone: '', web: '', note: '',
      pricingTier: 'velko'
    };

    const modalId = 'customer-editor-modal';
    this.removeModal(modalId);

    const html = `
      <div id="${modalId}" class="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
        <div class="flex items-end sm:items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
          <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" data-close="1"></div>
          <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
            <form id="customer-form" class="p-6">
              <div class="flex items-start justify-between mb-4">
                <h3 class="text-lg font-semibold text-gray-900">
                  ${isEdit ? 'Upravit zákazníka' : 'Nový zákazník'}
                </h3>
                <button type="button" data-close="1" class="text-gray-400 hover:text-gray-600">
                  ${icon('x-mark', 'w-5 h-5')}
                </button>
              </div>

              ${isEdit ? '' : `
              <div class="mb-5 rounded-lg border border-blue-100 bg-gradient-to-br from-blue-50/60 to-indigo-50/40 p-4">
                <label class="flex items-center gap-2 text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2">
                  ${icon('magnifying-glass', 'w-4 h-4')}
                  Vyhledat z ARESu
                </label>
                <div class="relative">
                  <input type="text" id="ares-search-input" autocomplete="off"
                    placeholder="Zadejte IČO (8 číslic) nebo název firmy…"
                    class="w-full pl-3 pr-10 py-2 border border-blue-200 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white" />
                  <span id="ares-search-spinner" class="hidden absolute right-3 top-1/2 -translate-y-1/2">
                    ${icon('spinner', 'animate-spin h-4 w-4 text-blue-500')}
                  </span>
                  <div id="ares-search-results" class="hidden absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-72 overflow-y-auto"></div>
                </div>
                <p class="mt-2 text-xs text-gray-500">
                  Nalezený subjekt klikem vybereš a údaje se předvyplní. Můžeš je pak před uložením upravit.
                </p>
              </div>
              `}

              <div class="grid grid-cols-1 sm:grid-cols-6 gap-4">
                <div class="sm:col-span-4">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Název firmy / jméno *</label>
                  <input type="text" name="name" required value="${this.escape(data.name)}"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                </div>
                <div class="sm:col-span-2">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Typ</label>
                  <select name="type" class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                    <option value="company" ${data.type === 'company' ? 'selected' : ''}>Firma</option>
                    <option value="person" ${data.type === 'person' ? 'selected' : ''}>Fyzická osoba</option>
                  </select>
                </div>
                <div class="sm:col-span-2">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Cenová úroveň</label>
                  <select name="pricingTier" class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                    <option value="velko" ${data.pricingTier === 'velko' ? 'selected' : ''}>Velkoobchodní</option>
                    <option value="malo" ${data.pricingTier === 'malo' ? 'selected' : ''}>Maloobchodní</option>
                  </select>
                </div>

                <div class="sm:col-span-3">
                  <label class="block text-sm font-medium text-gray-700 mb-1">IČO</label>
                  <input type="text" name="ico" value="${this.escape(data.ico)}"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                </div>
                <div class="sm:col-span-3">
                  <label class="block text-sm font-medium text-gray-700 mb-1">DIČ</label>
                  <input type="text" name="dic" value="${this.escape(data.dic)}"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                </div>

                <div class="sm:col-span-6">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Ulice a číslo popisné</label>
                  <input type="text" name="address" value="${this.escape(data.address)}"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                </div>
                <div class="sm:col-span-2">
                  <label class="block text-sm font-medium text-gray-700 mb-1">PSČ</label>
                  <input type="text" name="zip" value="${this.escape(data.zip)}"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                </div>
                <div class="sm:col-span-2">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Město</label>
                  <input type="text" name="city" value="${this.escape(data.city)}"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                </div>
                <div class="sm:col-span-2">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Země</label>
                  <input type="text" name="country" value="${this.escape(data.country)}"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                </div>

                <div class="sm:col-span-3">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Kontaktní osoba</label>
                  <input type="text" name="contactPerson" value="${this.escape(data.contactPerson)}"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                </div>
                <div class="sm:col-span-3">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
                  <input type="text" name="phone" value="${this.escape(data.phone)}"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                </div>
                <div class="sm:col-span-3">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input type="email" name="email" value="${this.escape(data.email)}"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                </div>
                <div class="sm:col-span-3">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Web</label>
                  <input type="text" name="web" value="${this.escape(data.web)}"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                </div>
                <div class="sm:col-span-6">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Poznámka</label>
                  <textarea name="note" rows="2"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">${this.escape(data.note)}</textarea>
                </div>
              </div>

              <div id="customer-form-error" class="mt-3 text-sm text-red-600 hidden"></div>

              <div class="mt-6 flex justify-end gap-2">
                <button type="button" data-close="1"
                  class="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50">
                  Zrušit
                </button>
                <button type="submit"
                  class="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-sm font-medium text-white hover:bg-blue-700">
                  ${isEdit ? 'Uložit změny' : 'Vytvořit zákazníka'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    `;

    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;
    document.body.appendChild(wrapper);

    const modal = document.getElementById(modalId);
    modal.querySelectorAll('[data-close="1"]').forEach((el) => {
      el.addEventListener('click', () => this.removeModal(modalId));
    });

    const form = document.getElementById('customer-form');
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const fd = new FormData(form);
      const payload = Object.fromEntries(fd.entries());

      try {
        if (isEdit) {
          await this.updateCustomer(customer.id, payload);
          this.notify('Zákazník byl aktualizován', 'success');
        } else {
          await this.createCustomer(payload);
          this.notify('Zákazník byl vytvořen', 'success');
        }
        this.removeModal(modalId);
        await this.fetchCustomers(this.searchQuery);
        this.renderList();
      } catch (err) {
        const errBox = document.getElementById('customer-form-error');
        if (errBox) {
          errBox.textContent = err.message;
          errBox.classList.remove('hidden');
        }
      }
    });

    if (!isEdit) {
      this.bindAresSearch(form);
    }
  }

  // ----- ARES vyhledávání v editoru -----

  bindAresSearch(form) {
    const input = document.getElementById('ares-search-input');
    const resultsBox = document.getElementById('ares-search-results');
    const spinner = document.getElementById('ares-search-spinner');
    if (!input || !resultsBox) return;

    let debounceTimer = null;
    let lastQuery = '';
    let activeRequestToken = 0;

    const showResults = (html) => {
      resultsBox.innerHTML = html;
      resultsBox.classList.remove('hidden');
    };
    const hideResults = () => {
      resultsBox.classList.add('hidden');
      resultsBox.innerHTML = '';
    };

    const runSearch = async (query) => {
      const trimmed = query.trim();
      if (trimmed.length < 2) {
        hideResults();
        return;
      }
      if (trimmed === lastQuery) return;
      lastQuery = trimmed;

      const requestToken = ++activeRequestToken;
      spinner?.classList.remove('hidden');

      try {
        const results = await this.searchAres(trimmed, 10);
        if (requestToken !== activeRequestToken) return; // odpověď zastaralá

        if (!results.length) {
          showResults(`
            <div class="px-4 py-3 text-sm text-gray-500">
              Žádný subjekt nenalezen pro „${this.escape(trimmed)}".
            </div>
          `);
          return;
        }

        const html = results.map((r, i) => `
          <button type="button" data-ares-idx="${i}"
            class="w-full text-left px-4 py-3 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none border-b border-gray-100 last:border-b-0 transition-colors">
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0 flex-1">
                <div class="font-medium text-sm text-gray-900 truncate">
                  ${this.escape(r.name || '(bez názvu)')}
                </div>
                <div class="mt-0.5 text-xs text-gray-500 truncate">
                  ${this.escape(r.fullAddress || [r.address, r.city].filter(Boolean).join(', '))}
                </div>
              </div>
              <div class="text-right shrink-0">
                <div class="text-xs font-mono text-gray-700">IČO ${this.escape(r.ico)}</div>
                ${r.dic ? `<div class="text-[11px] text-gray-400">${this.escape(r.dic)}</div>` : ''}
                ${r.active === false ? '<div class="text-[11px] text-red-500">Zaniklý subjekt</div>' : ''}
              </div>
            </div>
          </button>
        `).join('');

        showResults(html);

        resultsBox.querySelectorAll('[data-ares-idx]').forEach((btn) => {
          btn.addEventListener('click', () => {
            const idx = Number(btn.dataset.aresIdx);
            const subject = results[idx];
            if (subject) this.applyAresSubject(form, subject);
            hideResults();
          });
        });
      } catch (err) {
        if (requestToken !== activeRequestToken) return;
        showResults(`
          <div class="px-4 py-3 text-sm text-red-600">
            ${this.escape(err.message || 'Chyba při vyhledávání v ARESu')}
          </div>
        `);
      } finally {
        if (requestToken === activeRequestToken) {
          spinner?.classList.add('hidden');
        }
      }
    };

    input.addEventListener('input', () => {
      const value = input.value;
      if (debounceTimer) clearTimeout(debounceTimer);
      // U IČO (8 číslic) reagujeme okamžitě, jinak debounce 350 ms
      const delay = /^\d{8}$/.test(value.trim()) ? 0 : 350;
      debounceTimer = setTimeout(() => runSearch(value), delay);
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (debounceTimer) clearTimeout(debounceTimer);
        runSearch(input.value);
      } else if (e.key === 'Escape') {
        hideResults();
      }
    });

    // Klik mimo dropdown ho zavře
    document.addEventListener('click', (e) => {
      if (!resultsBox.contains(e.target) && e.target !== input) {
        hideResults();
      }
    });
  }

  applyAresSubject(form, subject) {
    if (!form || !subject) return;
    const setValue = (name, value) => {
      const el = form.querySelector(`[name="${name}"]`);
      if (el && value !== undefined && value !== null && value !== '') {
        el.value = value;
        // Vizuální flash že se hodnota doplnila
        el.classList.add('ring-2', 'ring-green-300');
        setTimeout(() => el.classList.remove('ring-2', 'ring-green-300'), 1200);
      }
    };

    setValue('name', subject.name);
    setValue('ico', subject.ico);
    setValue('dic', subject.dic);
    setValue('address', subject.address);
    setValue('city', subject.city);
    setValue('zip', subject.zip);
    setValue('country', subject.country);

    const typeEl = form.querySelector('[name="type"]');
    if (typeEl) typeEl.value = 'company';

    this.notify(`Údaje načteny z ARESu (${subject.name})`, 'success');
  }

  async confirmDelete(id) {
    const customer = this.customers.find((c) => c.id === id);
    if (!customer) return;
    if (!confirm(`Opravdu smazat zákazníka „${customer.name}"?`)) return;

    try {
      await this.deleteCustomer(id);
      this.notify('Zákazník byl smazán', 'success');
      await this.fetchCustomers(this.searchQuery);
      this.renderList();
    } catch (err) {
      this.notify(err.message, 'error');
    }
  }

  // ----- Selektor pro editor nabídek -----

  /**
   * Otevře modální vyhledávač zákazníků.
   * Vrací Promise<customer|null>.
   */
  openSelector() {
    return new Promise(async (resolve) => {
      const modalId = 'customer-selector-modal';
      this.removeModal(modalId);

      const html = `
        <div id="${modalId}" class="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
          <div class="flex items-end sm:items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" data-close="1"></div>
            <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-xl sm:w-full">
              <div class="p-6">
                <div class="flex items-start justify-between mb-4">
                  <h3 class="text-lg font-semibold text-gray-900">Vybrat zákazníka</h3>
                  <button type="button" data-close="1" class="text-gray-400 hover:text-gray-600">
                    ${icon('x-mark', 'w-5 h-5')}
                  </button>
                </div>

                <div class="relative mb-3">
                  <input type="text" id="customer-selector-search"
                    placeholder="Hledat (název, IČO, město, email)…"
                    class="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                  ${icon('magnifying-glass', 'absolute left-2.5 top-2.5 w-4 h-4 text-gray-400')}
                </div>

                <div id="customer-selector-results" class="max-h-96 overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-200">
                  ${this.renderLoading()}
                </div>

                <div class="mt-4 flex justify-between items-center text-sm">
                  <button type="button" id="customer-selector-add-new"
                    class="inline-flex items-center text-blue-600 hover:text-blue-800">
                    ${icon('plus', 'w-4 h-4 mr-1')}
                    Přidat nového zákazníka
                  </button>
                  <button type="button" data-close="1"
                    class="inline-flex justify-center rounded-md border border-gray-300 px-3 py-1.5 bg-white text-gray-700 hover:bg-gray-50">
                    Zrušit
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      `;

      const wrapper = document.createElement('div');
      wrapper.innerHTML = html;
      document.body.appendChild(wrapper);

      const modal = document.getElementById(modalId);
      const close = (value) => {
        this.removeModal(modalId);
        resolve(value);
      };

      modal.querySelectorAll('[data-close="1"]').forEach((el) => {
        el.addEventListener('click', () => close(null));
      });

      const renderResults = (items) => {
        const results = document.getElementById('customer-selector-results');
        if (!results) return;
        if (!items || items.length === 0) {
          results.innerHTML = `<div class="p-4 text-sm text-gray-500 text-center">Nic nenalezeno</div>`;
          return;
        }
        results.innerHTML = items
          .map(
            (c) => `
            <button type="button" data-id="${c.id}"
              class="customer-selector-item w-full text-left px-4 py-3 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none">
              <div class="flex items-center justify-between gap-2">
                <div class="min-w-0">
                  <div class="text-sm font-semibold text-gray-900 truncate">${this.escape(c.name)}</div>
                  <div class="text-xs text-gray-500 truncate">
                    ${[c.ico ? `IČO ${this.escape(c.ico)}` : '', c.city ? this.escape(c.city) : '', c.email ? this.escape(c.email) : '']
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                </div>
                <span class="text-xs px-2 py-0.5 rounded-full ${c.type === 'person' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}">
                  ${c.type === 'person' ? 'Osoba' : 'Firma'}
                </span>
              </div>
            </button>
          `
          )
          .join('');

        results.querySelectorAll('.customer-selector-item').forEach((btn) => {
          btn.addEventListener('click', () => {
            const id = btn.dataset.id;
            const found = items.find((x) => x.id === id);
            close(found || null);
          });
        });
      };

      try {
        await this.fetchCustomers('');
        renderResults(this.customers);
      } catch (err) {
        const results = document.getElementById('customer-selector-results');
        if (results) {
          results.innerHTML = `<div class="p-4 text-sm text-red-600 text-center">${this.escape(err.message)}</div>`;
        }
      }

      const searchInput = document.getElementById('customer-selector-search');
      if (searchInput) {
        let debounce;
        searchInput.focus();
        searchInput.addEventListener('input', (e) => {
          clearTimeout(debounce);
          const value = e.target.value;
          debounce = setTimeout(async () => {
            try {
              const items = await this.fetchCustomers(value);
              renderResults(items);
            } catch (err) {
              const results = document.getElementById('customer-selector-results');
              if (results) {
                results.innerHTML = `<div class="p-4 text-sm text-red-600 text-center">${this.escape(err.message)}</div>`;
              }
            }
          }, 200);
        });
      }

      const addNewBtn = document.getElementById('customer-selector-add-new');
      if (addNewBtn) {
        addNewBtn.addEventListener('click', () => {
          close(null);
          // Po zavření selektoru otevři editor
          this.openEditor();
        });
      }
    });
  }

  // ----- Helpers -----

  /**
   * Z dat zákazníka vyrobí víceřádkový text pro vyplnění do textarea Odběratel.
   */
  static formatCustomerToText(c) {
    if (!c) return '';
    const lines = [];
    if (c.name) lines.push(c.name);
    if (c.address) lines.push(c.address);
    const cityLine = [c.zip, c.city].filter(Boolean).join(' ');
    if (cityLine) lines.push(cityLine);
    if (c.country && c.country !== 'Česká republika') lines.push(c.country);
    const taxLine = [c.ico ? `IČ: ${c.ico}` : '', c.dic ? `DIČ: ${c.dic}` : '']
      .filter(Boolean)
      .join(', ');
    if (taxLine) {
      lines.push('');
      lines.push(taxLine);
    }
    return lines.join('\n');
  }

  removeModal(id) {
    const el = document.getElementById(id);
    if (el && el.parentElement) el.parentElement.remove();
  }

  notify(message, type = 'success') {
    if (typeof window.showNotification === 'function') {
      window.showNotification(message, type);
    }
  }

  escape(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

window.CustomersManager = CustomersManager;
